import {
  addDays,
  checkinItemChanges,
  checkinLogId,
  epochOfLocalDateTime,
  earliestDoneAtOf,
  isLegacyLog,
  isLocalDateTime,
  isTimedKey,
  isValidDate,
  isValidPlannedMin,
  keyOnRuleDay,
  localDateTimeOf,
  logOccurrenceKey,
  occurrencesInPersonalDay,
  personalDateOf,
  personalDateOfKey,
  readCheckinSchedule,
  ruleDayOfKey,
  sameJsonValue,
  scheduleAfterChange,
  scheduleFieldOf,
  slotTimesOf,
  withRevision,
  normalizeCheckinRecurrence,
  validateCheckinRecurrence,
  type CheckinItemRecord,
  type CheckinLogData,
  type CheckinLogRecord,
  type LiveCheckinItem,
  type LocalDateTime,
  type OccurrenceKey,
  type ScheduleVersion,
  RecordType,
} from '@wiztime/core';
import { CheckinInUseError, ValidationError } from '../errors';
import type { WriteSession } from './session';

export interface CheckinItemInput {
  /** Omit to create a new item. */
  readonly id?: string;
  readonly name: string;
  readonly active?: boolean;
  readonly sort?: number;
  /**
   * Planned time of day, wall-clock minutes 0–1439 (2026-10b §8.1). Omit to keep the stored
   * value; null clears it.
   */
  readonly plannedMin?: number | null;
  /**
   * Local date-time from which `plannedMin` takes effect (data dictionary §3.2): occurrences
   * planned before it keep their earlier time. With an unchanged planned time it moves where
   * the current version starts. null corrects every date: completions move to the new time of
   * their rule day. Omit to change the current version only. Ignored for a new item.
   */
  readonly plannedFromAt?: LocalDateTime | null;
  /**
   * Recurrence rule (data dictionary §3). Each field: omit to keep the stored value; null clears
   * it. When any of the five is given, the merged result is validated and normalized (seven
   * weekdays → every day, fields irrelevant to the repeat → null) and all five are written in
   * the same record, so one save is one undo step.
   */
  readonly repeat?: string | null;
  readonly weekdays?: readonly number[] | null;
  readonly intervalDays?: number | null;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
  /** Picked for reminders under the `selected` scope (data dictionary §3). Omit to keep the stored value. */
  readonly remind?: boolean;
}

const RULE_KEYS = ['repeat', 'weekdays', 'intervalDays', 'startDate', 'endDate'] as const;

/** The recurrence fields to write, or null when the input leaves the rule untouched. */
function recurrenceUpdate(
  input: CheckinItemInput,
  current: Partial<Record<(typeof RULE_KEYS)[number], unknown>> | null,
) {
  if (RULE_KEYS.every((key) => input[key] === undefined)) return null;
  const pick = <K extends (typeof RULE_KEYS)[number]>(key: K) =>
    input[key] === undefined ? (current?.[key] ?? null) : input[key];
  const merged = {
    repeat: pick('repeat') as string | null,
    weekdays: pick('weekdays') as readonly number[] | null,
    intervalDays: pick('intervalDays') as number | null,
    startDate: pick('startDate') as string | null,
    endDate: pick('endDate') as string | null,
  };
  const issues = validateCheckinRecurrence(merged);
  if (issues.length > 0) throw new ValidationError('invalid recurrence rule', issues);
  return normalizeCheckinRecurrence(merged);
}

/** The live, non-deleted logs of one item. */
async function itemLogs(
  session: WriteSession,
  itemId: string,
): Promise<(CheckinLogRecord & { data: CheckinLogData })[]> {
  return (await session.tx.listByType(RecordType.CheckinLog)).filter(
    (log): log is CheckinLogRecord & { data: CheckinLogData } =>
      log.data !== null && log.data.itemId === itemId,
  );
}

/** The occurrence fields of a log record for `key` under day start `dayStartMin`. */
function occurrenceFields(key: OccurrenceKey, dayStartMin: number) {
  return isTimedKey(key)
    ? { personalDate: personalDateOfKey(key, dayStartMin), occursAt: key, date: undefined }
    : { personalDate: key, date: key, occursAt: undefined };
}

/** A log's data with the occurrence fields of `key` (unknown fields kept, stale ones dropped). */
function relocated(data: CheckinLogData, key: OccurrenceKey, dayStartMin: number): CheckinLogData {
  const { occursAt: _occursAt, date: _date, ...rest } = data;
  const fields = occurrenceFields(key, dayStartMin);
  return {
    ...rest,
    personalDate: fields.personalDate,
    ...(fields.occursAt === undefined ? {} : { occursAt: fields.occursAt }),
    ...(fields.date === undefined ? {} : { date: fields.date }),
  };
}

/**
 * "All dates" correction (data dictionary §3.2): every completion of the item moves to the
 * occurrence `next` gives its rule day. Of logs meeting on one occurrence a done one survives,
 * then the newer.
 */
async function remapLogs(
  session: WriteSession,
  itemId: string,
  previous: readonly ScheduleVersion[],
  next: readonly ScheduleVersion[],
  dayStartMin: number,
): Promise<void> {
  const logs = await itemLogs(session, itemId);
  type Log = (typeof logs)[number];
  // Records of one occurrence (a legacy and a current one): the newer is its state.
  const current = new Map<string, Log>();
  for (const log of logs) {
    const key = logOccurrenceKey(log.data, previous);
    const held = current.get(key);
    if (held === undefined || held.hlc < log.hlc) current.set(key, log);
  }
  // Two occurrences meeting on one (the transition day): a completion is never replaced by a
  // not-done record; between equals the newer survives.
  const wins = (log: Log, held: Log): boolean =>
    log.data.done !== held.data.done ? log.data.done : held.hlc < log.hlc;
  const targets = new Map<string, Log>();
  for (const [key, log] of current) {
    const ruleDay = ruleDayOfKey(previous, key);
    const target = ruleDay === null ? key : (keyOnRuleDay(next, ruleDay) ?? key);
    const held = targets.get(target);
    if (held === undefined || wins(log, held)) targets.set(target, log);
  }
  const kept = new Map([...targets].map(([target, log]) => [checkinLogId(itemId, target), { target, log }]));
  // Moved away, or lost to a newer log of the same occurrence (ids kept are rewritten below).
  for (const log of logs) if (!kept.has(log.id)) await session.write(RecordType.CheckinLog, log.id, null);
  for (const [id, { target, log }] of kept) {
    const data = relocated(log.data, target, dayStartMin);
    if (id === log.id && sameJsonValue(data, log.data)) continue;
    await session.write(RecordType.CheckinLog, id, data);
  }
}

/** The plan versions to store for an edit, or undefined when the plan is not touched. */
function scheduleUpdate(
  input: CheckinItemInput,
  current: CheckinItemRecord['data'] | null,
  versions: readonly ScheduleVersion[],
  dayStartMin: number,
): ScheduleVersion[] | undefined {
  if (input.plannedMin === undefined) return undefined;
  const plannedMin = input.plannedMin;
  const sameTimes = (version: ScheduleVersion | undefined) =>
    version !== undefined &&
    sameJsonValue(
      version.times.map((t) => t.min),
      plannedMin === null ? [] : [plannedMin],
    );
  if (current === null || input.plannedFromAt === null) {
    return [{ from: null, times: slotTimesOf(plannedMin, dayStartMin), dayStartMin }];
  }
  const last = versions.at(-1)!;
  if (input.plannedFromAt === undefined) {
    if (sameTimes(last)) return undefined;
    return [...versions.slice(0, -1), { ...last, times: slotTimesOf(plannedMin, last.dayStartMin) }];
  }
  // Same time: move where the current version starts; otherwise start a new version.
  const base = sameTimes(last) && last.from !== null ? versions.slice(0, -1) : versions;
  if (sameTimes(last) && last.from === null) return undefined;
  return scheduleAfterChange(base, input.plannedFromAt, plannedMin, dayStartMin);
}

async function nextSort(session: WriteSession): Promise<number> {
  const items = await session.tx.listByType(RecordType.CheckinItem);
  return items.reduce((max, item) => Math.max(max, (item.data?.sort ?? -1) + 1), 0);
}

/** Create a check-in item, or update the live item with the same id. */
export async function upsertCheckinItem(
  session: WriteSession,
  input: CheckinItemInput,
): Promise<CheckinItemRecord> {
  const existing = input.id === undefined ? null : await session.find(input.id);
  const current =
    existing !== null && existing.type === RecordType.CheckinItem && existing.data !== null
      ? existing.data
      : null;
  if (input.plannedMin !== undefined && input.plannedMin !== null && !isValidPlannedMin(input.plannedMin)) {
    throw new ValidationError('plannedMin must be an integer in 0–1439', [String(input.plannedMin)]);
  }
  if (input.plannedFromAt != null && !isLocalDateTime(input.plannedFromAt)) {
    throw new ValidationError('plannedFromAt must be a local date-time', [input.plannedFromAt]);
  }
  const recurrence = recurrenceUpdate(input, current);
  const id = input.id ?? session.newId();
  const settings = await session.settings();
  const versions = current === null ? [] : readCheckinSchedule(current, settings.checkinLegacyDayStartMin);
  const nextVersions = scheduleUpdate(input, current, versions, settings.dayStartMin);
  const projection = nextVersions === undefined ? undefined : scheduleFieldOf(nextVersions);
  // Spread the stored data first, then override the known fields, so fields this build does not
  // know (a newer client's plannedMin, for one) survive a local edit (2026-10b §2.2, stage one).
  const next = {
    ...current,
    name: input.name,
    active: input.active ?? current?.active ?? true,
    sort: input.sort ?? current?.sort ?? (await nextSort(session)),
    // A new item without a plan stays free of the field; an edit keeps whatever is stored.
    ...(projection === undefined
      ? {}
      : {
          plannedMin: projection.legacy.plannedMin,
          planHistory: projection.legacy.planHistory,
          schedule: projection,
        }),
    ...(input.remind === undefined ? {} : { remind: input.remind }),
    // The rule is written whole or not at all; absent keeps the stored fields via the spread.
    ...(recurrence ?? {}),
  };
  // A save that changes nothing writes nothing: no undo step, the last-updated time stays.
  if (current !== null && sameJsonValue(next, current)) return existing as CheckinItemRecord;
  const changes = current === null ? [] : checkinItemChanges(current, next, input.plannedFromAt);
  if (current !== null && input.plannedFromAt === null && nextVersions !== undefined) {
    await remapLogs(session, id, versions, nextVersions, settings.dayStartMin);
  }
  return session.write(
    RecordType.CheckinItem,
    id,
    withRevision(current, next, changes, new Date(session.nowMs).toISOString()),
  );
}

/** Today's personal date by the session clock and the stored day start. */
async function todayOf(
  session: WriteSession,
): Promise<{ readonly today: string; readonly dayStartMin: number }> {
  const { dayStartMin } = await session.settings();
  return { today: personalDateOf(session.nowMs, dayStartMin), dayStartMin };
}

type LiveLog = CheckinLogRecord & { data: CheckinLogData };

/** Where one occurrence's completion lives (data dictionary §3.2). */
interface OccurrenceLog {
  readonly item: LiveCheckinItem;
  readonly key: OccurrenceKey;
  /** The record holding its current state (the newer of a legacy and a new one), or null. */
  readonly log: LiveLog | null;
  /** Legacy records of the occurrence at another id: tombstoned when it is written. */
  readonly legacyIds: readonly string[];
  /** A legacy record of another occurrence sitting on this occurrence's id: moved first. */
  readonly squatter: { readonly log: LiveLog; readonly key: OccurrenceKey } | null;
  readonly dayStartMin: number;
}

const asLiveLog = (record: unknown): LiveLog | null => {
  const log = record as CheckinLogRecord | null;
  return log !== null && log.type === RecordType.CheckinLog && log.data !== null ? (log as LiveLog) : null;
};

/**
 * Locate the completion of occurrence `key` of `itemId`: the record at its id and the legacy
 * records (located by personal date only) that the old model gave the same occurrence. A legacy
 * record maps to the earliest version's occurrence on its personal date as a rule day, so the
 * candidates are the key's date and the day before.
 */
async function locate(session: WriteSession, itemId: string, key: OccurrenceKey): Promise<OccurrenceLog> {
  if (!isTimedKey(key) && !isValidDate(key)) throw new ValidationError('Invalid occurrence', [key]);
  const item: LiveCheckinItem = await session.require(RecordType.CheckinItem, itemId);
  const settings = await session.settings();
  const versions = readCheckinSchedule(item.data, settings.checkinLegacyDayStartMin);
  const id = checkinLogId(itemId, key);
  const atId = asLiveLog(await session.find(id));
  let squatter: OccurrenceLog['squatter'] = null;
  const matching: LiveLog[] = [];
  if (atId !== null) {
    const atKey = logOccurrenceKey(atId.data, versions);
    if (atKey === key) matching.push(atId);
    else squatter = { log: atId, key: atKey };
  }
  const date = key.slice(0, 10);
  const legacyIds: string[] = [];
  for (const personalDate of isTimedKey(key) ? [date, addDays(date, -1)] : []) {
    const legacy = asLiveLog(await session.find(checkinLogId(itemId, personalDate)));
    if (legacy === null || !isLegacyLog(legacy.data) || logOccurrenceKey(legacy.data, versions) !== key)
      continue;
    matching.push(legacy);
    legacyIds.push(legacy.id);
  }
  const log = matching.reduce<LiveLog | null>(
    (held, next) => (held === null || held.hlc < next.hlc ? next : held),
    null,
  );
  // A new record only for an occurrence the schedule produces: a bare date on a timed item, or a
  // time the item never had, would show as a stray row. Existing records stay editable.
  const produced = isTimedKey(key)
    ? ruleDayOfKey(versions, key) !== null
    : keyOnRuleDay(versions, key) === key;
  if (log === null && atId === null && !produced) {
    throw new ValidationError('Not an occurrence of the check-in item', [key]);
  }
  return { item, key, log, legacyIds, squatter, dayStartMin: settings.dayStartMin };
}

/**
 * Write the state of a located occurrence at its own id, in occurrence form. A legacy record of
 * another occurrence on that id moves to its own id first; legacy records of this occurrence
 * elsewhere are tombstoned. One undo step with the caller's command.
 */
async function writeOccurrence(
  session: WriteSession,
  located: OccurrenceLog,
  state: { readonly done: boolean; readonly doneAt: string | null },
): Promise<CheckinLogRecord> {
  const { item, key, log, legacyIds, squatter, dayStartMin } = located;
  if (squatter !== null) {
    // Moved only over an older (or no) record of its own occurrence; a newer tombstone stays.
    const ownId = checkinLogId(item.id, squatter.key);
    const own = await session.find(ownId);
    if (own === null || own.hlc < squatter.log.hlc) {
      await session.write(
        RecordType.CheckinLog,
        ownId,
        relocated(squatter.log.data, squatter.key, dayStartMin),
      );
    }
  }
  const base: CheckinLogData = log?.data ?? {
    itemId: item.id,
    personalDate: key.slice(0, 10),
    done: false,
    doneAt: null,
  };
  const written = await session.write(RecordType.CheckinLog, checkinLogId(item.id, key), {
    ...relocated(base, key, dayStartMin),
    itemId: item.id,
    done: state.done,
    doneAt: state.doneAt,
  });
  for (const id of legacyIds) if (id !== written.id) await session.write(RecordType.CheckinLog, id, null);
  return written;
}

/** Whether any occurrence of the item on `today` is done (disabling keeps the day then). */
async function isDoneOn(session: WriteSession, item: LiveCheckinItem, today: string): Promise<boolean> {
  const settings = await session.settings();
  const versions = readCheckinSchedule(item.data, settings.checkinLegacyDayStartMin);
  for (const slot of occurrencesInPersonalDay(item.data, versions, today, settings.dayStartMin)) {
    if ((await locate(session, item.id, slot.key)).log?.data.done === true) return true;
  }
  return false;
}

/**
 * Disable or re-enable a check-in item (data dictionary §3.2). Disabling records `disabledAt` =
 * now: occurrences planned from now on stop, earlier ones keep showing, missed ones included.
 * `disabledFrom` (today, tomorrow when today is ticked) is still written for older clients.
 * Re-enabling clears both. The rule fields are not touched. One write, one undo step.
 */
export async function setCheckinItemActive(
  session: WriteSession,
  id: string,
  active: boolean,
): Promise<CheckinItemRecord> {
  const item: LiveCheckinItem = await session.require(RecordType.CheckinItem, id);
  if (item.data.active === active) return item;
  let disabledFrom: string | null = null;
  let disabledAt: LocalDateTime | null = null;
  if (!active) {
    const { today } = await todayOf(session);
    disabledFrom = (await isDoneOn(session, item, today)) ? addDays(today, 1) : today;
    disabledAt = localDateTimeOf(session.nowMs);
  }
  const next = { ...item.data, active, disabledFrom, disabledAt };
  const at = new Date(session.nowMs).toISOString();
  return session.write(
    RecordType.CheckinItem,
    id,
    withRevision(item.data, next, checkinItemChanges(item.data, next), at),
  );
}

/**
 * Star or unstar a check-in item. Only the settings list order depends on it; it is not a tracked
 * revision field (data dictionary §3.1). One write, one undo step; no write when nothing changes.
 */
export async function setCheckinItemStarred(
  session: WriteSession,
  id: string,
  starred: boolean,
): Promise<CheckinItemRecord> {
  const item: LiveCheckinItem = await session.require(RecordType.CheckinItem, id);
  if ((item.data.starred === true) === starred) return item;
  return session.write(RecordType.CheckinItem, id, { ...item.data, starred });
}

/**
 * Delete a check-in item that has never been completed. An item with a completed log is history
 * and can only be disabled (CheckinInUseError); logs of a deleted item stay in storage.
 */
export async function deleteCheckinItem(session: WriteSession, id: string): Promise<void> {
  await session.require(RecordType.CheckinItem, id);
  const done = (await itemLogs(session, id)).filter((log) => log.data.done).length;
  if (done > 0) throw new CheckinInUseError(id, done);
  await session.write(RecordType.CheckinItem, id, null);
}

/**
 * Tick occurrence `key` of `itemId` unless it is ticked already (the "Done" button of a
 * reminder: a repeated or late click must never untick). Returns the written log, or null when
 * nothing changed, which leaves no undo step.
 */
export async function markCheckinDone(
  session: WriteSession,
  itemId: string,
  key: OccurrenceKey,
): Promise<CheckinLogRecord | null> {
  const located = await locate(session, itemId, key);
  if (located.log?.data.done === true) return null;
  // Done from a reminder means done now, even for a missed reminder of an earlier day; a lead
  // time can fire it before the personal day starts, so it is never earlier than the day start.
  const doneAtMs = Math.max(session.nowMs, earliestDoneAtOf(key, located.dayStartMin));
  return writeOccurrence(session, located, { done: true, doneAt: new Date(doneAtMs).toISOString() });
}

/**
 * Default completion instant of a tick (data dictionary §3, checkinLog.doneAt): now on today's
 * personal date; on an earlier date the planned instant, or null without a planned time; on a
 * later date null.
 */
async function defaultDoneAt(session: WriteSession, key: OccurrenceKey): Promise<string | null> {
  const { today, dayStartMin } = await todayOf(session);
  const personalDate = personalDateOfKey(key, dayStartMin);
  if (personalDate === today) return new Date(session.nowMs).toISOString();
  if (personalDate > today || !isTimedKey(key)) return null;
  return new Date(epochOfLocalDateTime(key)).toISOString();
}

/**
 * Flip the check-in of occurrence `key` (a planned local date-time, or a date when untimed). The
 * log has a deterministic id, so every device addresses the same record; unchecking keeps the
 * record with `done` false. A tick records the default completion instant, or `doneAtMs` when
 * given. A legacy record of the occurrence is rewritten in occurrence form in the same step.
 */
export async function toggleCheckin(
  session: WriteSession,
  itemId: string,
  key: OccurrenceKey,
  doneAtMs?: number,
): Promise<CheckinLogRecord> {
  const located = await locate(session, itemId, key);
  const done = located.log?.data.done !== true;
  const doneAt = !done
    ? null
    : doneAtMs !== undefined
      ? new Date(doneAtMs).toISOString()
      : await defaultDoneAt(session, key);
  return writeOccurrence(session, located, { done, doneAt });
}

/**
 * Set the actual completion instant of a done occurrence (checkin-plan-change.md §3.2), or clear
 * it with null. The instant must not be earlier than `earliestDoneAtOf` nor later than now. One
 * undo step.
 */
export async function setCheckinDoneAt(
  session: WriteSession,
  itemId: string,
  key: OccurrenceKey,
  doneAt: string | null,
): Promise<CheckinLogRecord> {
  const located = await locate(session, itemId, key);
  const log = located.log;
  if (log === null || !log.data.done) {
    throw new ValidationError('The check-in is not done', [checkinLogId(itemId, key)]);
  }
  let iso: string | null = null;
  if (doneAt !== null) {
    const ms = Date.parse(doneAt);
    if (Number.isNaN(ms)) throw new ValidationError('doneAt must be an ISO instant', [doneAt]);
    if (ms < earliestDoneAtOf(key, located.dayStartMin) || ms > session.nowMs) {
      throw new ValidationError(
        'doneAt must lie between the earliest completion time of the occurrence and now',
        [doneAt],
      );
    }
    iso = new Date(ms).toISOString();
  }
  const inPlace =
    log.id === checkinLogId(itemId, key) && located.legacyIds.length === 0 && !isLegacyLog(log.data);
  if (inPlace && log.data.doneAt === iso) return log;
  return writeOccurrence(session, located, { done: true, doneAt: iso });
}

/** One row of the check-in card's edit mode. */
export interface CheckinStateChange {
  readonly itemId: string;
  readonly occurrence: OccurrenceKey;
  readonly done: boolean;
  /** ISO instant, or null for "done, time not recorded"; ignored when not done. */
  readonly doneAt: string | null;
}

/**
 * Set the completion state of several occurrences at once (the card's edit mode, plan §3.5): all
 * rows are validated first, then written as one undo step. Rows whose state does not change
 * write nothing. Returns the number of logs written.
 */
export async function setCheckinStates(
  session: WriteSession,
  changes: readonly CheckinStateChange[],
): Promise<number> {
  const located = [];
  for (const change of changes) {
    const occurrence = await locate(session, change.itemId, change.occurrence);
    let doneAt: string | null = null;
    if (change.done && change.doneAt !== null) {
      const ms = Date.parse(change.doneAt);
      if (Number.isNaN(ms)) throw new ValidationError('doneAt must be an ISO instant', [change.doneAt]);
      if (ms < earliestDoneAtOf(change.occurrence, occurrence.dayStartMin) || ms > session.nowMs) {
        throw new ValidationError(
          'doneAt must lie between the earliest completion time of the occurrence and now',
          [change.doneAt],
        );
      }
      doneAt = new Date(ms).toISOString();
    }
    located.push({ change, done: change.done, doneAt });
  }
  let written = 0;
  // Each row is located again after the rows before it are written: two rows of one item may
  // share a record (a legacy record moved by the first).
  for (const { change, done, doneAt } of located) {
    const occurrence = await locate(session, change.itemId, change.occurrence);
    const stored = occurrence.log?.data;
    const wasDone = stored?.done === true;
    if (!done && !wasDone) continue;
    if (
      done &&
      wasDone &&
      (stored?.doneAt ?? null) === doneAt &&
      occurrence.legacyIds.length === 0 &&
      !isLegacyLog(stored!)
    ) {
      continue;
    }
    await writeOccurrence(session, occurrence, { done, doneAt: done ? doneAt : null });
    written += 1;
  }
  return written;
}
